import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  GraduationCap, 
  Edit, 
  Save, 
  Plus, 
  Calendar, 
  Trash2 
} from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '@/config/api';

const toast = {
  success: (message: string) => {
    console.log('✅ Success:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 4000);
  },
  error: (message: string) => {
    console.error('❌ Error:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 5000);
  }
};

interface EducationTabProps {
  data: any;
  loading: boolean;
  isEditable: boolean;
  editMode: boolean;
  onEdit: () => void;
  onSave: (data: any) => void;
  onCancel: () => void;
  onRefresh?: () => void;
}

const EducationTab: React.FC<EducationTabProps> = ({ 
  data, 
  loading, 
  isEditable, 
  editMode, 
  onEdit, 
  onSave, 
  onCancel,
  onRefresh 
}) => {
  const [editData, setEditData] = useState(data);
  const updateEditData = (updater: (prev: any) => any) => setEditData((prev: any) => updater(prev));
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEducation, setNewEducation] = useState({
    degree: '',
    field_of_study: '',
    institution: '',
    location: '',
    start_date: '',
    end_date: '',
    is_current: false,
    grade: '',
    description: '',
    activities: ''
  });

  useEffect(() => {
    setEditData(data);
  }, [data]);

  if (loading) {
    return <Card><CardContent className="p-6">Loading education...</CardContent></Card>;
  }

  const handleSave = () => {
    onSave(editData);
  };

  const handleCancel = () => {
    setEditData(data);
    onCancel();
  };

  const handleEducationChange = (index: number, field: string, value: any) => {
    updateEditData((prev: any) => ({
      ...prev,
      education: prev?.education?.map((edu: any, i: number) => 
        i === index ? { ...edu, [field]: value } : edu
      ) || []
    }));
  };

  const handleAddNewEducation = async () => {
    if (!newEducation.degree.trim()) {
      toast.error('Degree is required');
      return;
    }
    
    if (!newEducation.institution.trim()) {
      toast.error('Institution is required');
      return;
    }
    
    if (!newEducation.field_of_study.trim()) {
      toast.error('Field of study is required');
      return;
    }
    
    if (!newEducation.start_date.trim()) {
      toast.error('Start date is required');
      return;
    }

    try {
      const url = buildApiUrl('education/me/education');
      const headers = getApiHeaders(true);
      
      console.log('🔍 Education API Debug - URL:', url);
      console.log('🔍 Education API Debug - Headers:', headers);
      console.log('🔍 Education API Debug - Payload:', newEducation);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(newEducation)
      });

      console.log('🔍 Education API Debug - Response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to add education';
        try {
          const errorData = await response.json();
          if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to add education.';
          } else if (response.status === 422) {
            if (errorData.detail && Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) => 
                typeof err === 'string' ? err : 
                err.msg || err.message || JSON.stringify(err)
              ).join(', ');
            } else {
              errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
            }
          } else {
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Education record added successfully');
        setNewEducation({
          degree: '',
          field_of_study: '',
          institution: '',
          location: '',
          start_date: '',
          end_date: '',
          is_current: false,
          grade: '',
          description: '',
          activities: ''
        });
        setShowAddForm(false);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to add education');
      }
    } catch (error: any) {
      console.error('❌ Error adding education:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.toString && typeof error.toString === 'function') {
        try {
          errorMessage = error.toString();
        } catch (e) {
          errorMessage = 'Error converting error to string';
        }
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Error serializing error object';
        }
      }
      
      toast.error(`Error adding education: ${errorMessage}`);
    }
  };

  const handleDeleteEducation = async (educationId: number, educationTitle: string) => {
    console.log('🔍 handleDeleteEducation called with:', { educationId, educationTitle });
    
    if (!educationId || educationId === undefined || educationId === null) {
      toast.error('Cannot delete education: Invalid education ID');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the education record "${educationTitle}"?`)) {
      return;
    }

    try {
      const url = buildApiUrl(`education/me/education/${educationId}`);
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        let errorMessage = 'Failed to delete education';
        try {
          const errorData = await response.json();
          if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to delete education.';
          } else if (response.status === 422) {
            if (errorData.detail && Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) => 
                typeof err === 'string' ? err : 
                err.msg || err.message || JSON.stringify(err)
              ).join(', ');
            } else {
              errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
            }
          } else {
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Education record "${educationTitle}" deleted successfully`);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to delete education');
      }
    } catch (error: any) {
      console.error('❌ Error deleting education:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.toString && typeof error.toString === 'function') {
        try {
          errorMessage = error.toString();
        } catch (e) {
          errorMessage = 'Error converting error to string';
        }
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Error serializing error object';
        }
      }
      
      toast.error(`Error deleting education: ${errorMessage}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5" />
          Education ({data?.total_count || 0})
        </CardTitle>
        <div className="flex gap-2">
          {isEditable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddForm(true)}
              className="bg-green-50 hover:bg-green-100 border-green-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Education
            </Button>
          )}
          {isEditable && !editMode && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {data?.education?.length > 0 ? (
          <div className="space-y-4">
            {(editMode ? editData?.education : data.education)?.map((edu: any, index: number) => {
              console.log('🔍 Education item:', { id: edu.id, uuid: edu.uuid, degree: edu.degree, institution: edu.institution });
              return (
              <div key={edu.uuid || index} className="border rounded-lg p-4">
                {editMode ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-start gap-2">
                      <input
                        type="text"
                        value={edu.degree || ''}
                        onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                        className="font-semibold text-lg w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                        placeholder="Degree"
                      />
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={edu.is_current || false}
                            onChange={(e) => handleEducationChange(index, 'is_current', e.target.checked)}
                          />
                          <span>Current</span>
                        </label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEducation(edu.id, `${edu.degree} at ${edu.institution}`)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                        <input
                          type="text"
                          value={edu.institution || ''}
                          onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none bg-transparent"
                          placeholder="Institution"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Field of Study</label>
                        <input
                          type="text"
                          value={edu.field_of_study || ''}
                          onChange={(e) => handleEducationChange(index, 'field_of_study', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none bg-transparent"
                          placeholder="e.g., Computer Science"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                        <input
                          type="text"
                          value={edu.location || ''}
                          onChange={(e) => handleEducationChange(index, 'location', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none bg-transparent"
                          placeholder="City, Country"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Grade / CGPA</label>
                        <input
                          type="text"
                          value={edu.grade || ''}
                          onChange={(e) => handleEducationChange(index, 'grade', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none bg-transparent"
                          placeholder="e.g., 3.8 GPA"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={edu.start_date || ''}
                          onChange={(e) => handleEducationChange(index, 'start_date', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        {edu.is_current ? (
                          <div className="text-sm text-gray-500">Present</div>
                        ) : (
                          <input
                            type="date"
                            value={edu.end_date || ''}
                            onChange={(e) => handleEducationChange(index, 'end_date', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{edu.degree}</h3>
                      <Badge variant={edu.is_current ? "default" : "secondary"}>
                        {edu.is_current ? "Current" : "Completed"}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{edu.institution}</p>
                    <p className="text-sm">{edu.field_of_study}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <p className="text-sm text-gray-600">{edu.location}</p>
                      <p className="text-sm text-gray-600">{edu.grade}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{edu.start_date} - {edu.end_date || 'Present'}</span>
                    </div>
                  </>
                )}
              </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-gray-500">No education information available</p>
        )}

        {showAddForm && (
          <div className="border rounded-lg p-4 mt-4 bg-gray-50">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Education
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Degree *
                </label>
                <input
                  type="text"
                  value={newEducation.degree}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, degree: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Bachelor of Science, Master of Arts"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field of Study *
                </label>
                <input
                  type="text"
                  value={newEducation.field_of_study}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, field_of_study: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Computer Science, Business Administration"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Institution *
                </label>
                <input
                  type="text"
                  value={newEducation.institution}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, institution: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Harvard University"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={newEducation.location}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Cambridge, MA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade/GPA
                </label>
                <input
                  type="text"
                  value={newEducation.grade}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., 3.8 GPA, First Class Honors"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={newEducation.is_current}
                    onChange={(e) => setNewEducation(prev => ({ ...prev, is_current: e.target.checked, end_date: e.target.checked ? '' : prev.end_date }))}
                  />
                  <span className="text-sm font-medium text-gray-700">Currently Studying</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={newEducation.start_date}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              {!newEducation.is_current && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newEducation.end_date}
                    onChange={(e) => setNewEducation(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newEducation.description}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Brief description of your studies, thesis, or notable coursework..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activities and Societies
                </label>
                <textarea
                  value={newEducation.activities}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, activities: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Student organizations, clubs, sports teams, etc."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddNewEducation} className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Add Education
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        
        {editMode && isEditable && (
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EducationTab;
